import * as React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover.js';
import { Input } from './input.js';
import { Label } from './label.js';
import { Button } from './button.js';
import { PILL_VALUE_CLASS, PillWrapper, PillTriggerButton } from './filter-pill-primitives.js';
import type {
  RangeFilterField,
  FilterPanelLabels,
  RangePillLabels,
} from './filter-pill-primitives.js';

export type { RangePillLabels } from './filter-pill-primitives.js';

// ─── RangePill ────────────────────────────────────────────────
// Keyboard-first Min/Max numeric range pill — NO slider, NO preset chips.
// One-sided values are supported (min-only / max-only). See "HOW TO ADD A NEW
// FILTER PILL TYPE" (Option A) in filter-pill-primitives.tsx.

export interface RangePillProps {
  field: RangeFilterField;
  /** value[0] = min, value[1] = max (numeric strings; '' when that side is unset) */
  selected: string[];
  onChange: (id: string, values: string[]) => void;
  onClear: (id: string) => void;
  isLoading?: boolean;
  labels: FilterPanelLabels;
  rangeLabels: RangePillLabels;
  autoOpen?: boolean;
  onAutoOpened?: () => void;
}

/**
 * Formats a Min/Max pair into active-chip/pill text, handling the one-sided
 * cases. Returns `null` when both sides are blank (idle — nothing to show).
 */
export function formatRangeValue(min: string, max: string, unit?: string): string | null {
  const trimmedMin = min.trim();
  const trimmedMax = max.trim();
  const unitSuffix = unit ? ` ${unit}` : '';
  if (trimmedMin && trimmedMax) return `${trimmedMin}\u2013${trimmedMax}${unitSuffix}`;
  if (trimmedMin) return `${trimmedMin}+${unitSuffix}`;
  if (trimmedMax) return `\u2264 ${trimmedMax}${unitSuffix}`;
  return null;
}

export function RangePill({
  field,
  selected,
  onChange,
  onClear,
  isLoading,
  labels,
  rangeLabels,
  autoOpen,
  onAutoOpened,
}: RangePillProps) {
  const [open, setOpen] = React.useState(false);
  const minStr = selected[0] ?? '';
  const maxStr = selected[1] ?? '';
  const isActive = minStr.trim() !== '' || maxStr.trim() !== '';
  const minInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!autoOpen || open) return;
    // Delay opening so the AddFilterButton popover fully closes first,
    // otherwise the focus shift immediately dismisses this popover.
    const timer = window.setTimeout(() => {
      setOpen(true);
      onAutoOpened?.();
    }, 100);
    return () => window.clearTimeout(timer);
  }, [autoOpen, open]);

  function handleClear(e: React.MouseEvent | React.KeyboardEvent) {
    e.stopPropagation();
    onClear(field.id);
  }

  /** Enter commits (closes the popover) without touching already-typed values; Escape's
   * default close behavior already leaves state untouched, so it needs no handler here. */
  function handleCommitKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    setOpen(false);
  }

  const formattedText = formatRangeValue(minStr, maxStr, field.unit);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PillWrapper label={field.label} showLabel={isActive}>
        <PopoverTrigger asChild>
          <PillTriggerButton
            fieldId={field.id}
            isActive={isActive}
            isLoading={isLoading}
            open={open}
            idleLabel={field.label}
            activeContent={<span className={PILL_VALUE_CLASS}>{formattedText}</span>}
            onClear={handleClear}
            clearAriaLabel={labels.getClearAriaLabel(field.id)}
          />
        </PopoverTrigger>
      </PillWrapper>
      <PopoverContent
        className="w-72 p-3"
        align="start"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          minInputRef.current?.focus();
        }}
      >
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label htmlFor={`${field.id}-min`}>{rangeLabels.minLabel}</Label>
            <Input
              ref={minInputRef}
              id={`${field.id}-min`}
              type="number"
              inputMode="numeric"
              placeholder="0"
              value={minStr}
              onChange={(e) => onChange(field.id, [e.target.value, maxStr])}
              onKeyDown={handleCommitKeyDown}
              className="mt-1"
              data-testid={`${field.id}-min-input`}
            />
          </div>
          <span className="pb-2 text-muted-foreground">–</span>
          <div className="flex-1">
            <Label htmlFor={`${field.id}-max`}>{rangeLabels.maxLabel}</Label>
            <Input
              id={`${field.id}-max`}
              type="number"
              inputMode="numeric"
              placeholder="∞"
              value={maxStr}
              onChange={(e) => onChange(field.id, [minStr, e.target.value])}
              onKeyDown={handleCommitKeyDown}
              className="mt-1"
              data-testid={`${field.id}-max-input`}
            />
          </div>
          {field.unit ? (
            <span className="pb-2 text-xs text-muted-foreground">{field.unit}</span>
          ) : null}
        </div>
        <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t border-border/60">
          <Button type="button" variant="outline" size="xs" onClick={() => onClear(field.id)}>
            {rangeLabels.clearLabel}
          </Button>
          <Button type="button" size="xs" onClick={() => setOpen(false)}>
            {rangeLabels.applyLabel}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
